import { useContext, useMemo } from "react";

import { AuthContext } from "~/auth/AuthProvider";
import { FeatureFlagName } from "~/gql/graphqlBase.generated";
import { GetAllFeatureFlagsQuery, useGetAllFeatureFlagsQuery } from "~/gql/queries/featureFlag.generated";
import moduleFeatureFlags, { type ModuleEnabledMap } from "~/helpers/moduleFeatureFlags";
import { useAppSelector } from "~/hooks/reduxHooks";
import { type FlagKey } from "~/hooks/useFeatureFlag";
import useNetworkStatus from "~/hooks/useNetworkStatus";
import { type FeatureFlagCacheState, selectLastKnownFeatureFlag } from "~/store/featureFlagCacheSlice";

interface ModuleEnabledState {
	enabledByKey: ModuleEnabledMap;
	isSettled: boolean;
}

const moduleEntries = Object.entries(moduleFeatureFlags) as [string, FeatureFlagName][];

const selectModuleEnabledMap = (flags: GetAllFeatureFlagsQuery | undefined): ModuleEnabledMap =>
	Object.fromEntries(
		moduleEntries.map(([moduleKey, flagName]) => [moduleKey, flags?.[flagName as FlagKey]?.enabled ?? false])
	);

const selectLastKnownModuleEnabledMap = (featureFlagCache: FeatureFlagCacheState, tenantId: string): ModuleEnabledMap =>
	Object.fromEntries(
		moduleEntries.map(([moduleKey, flagName]) => [
			moduleKey,
			selectLastKnownFeatureFlag(flagName, tenantId)({ featureFlagCache }) === true
		])
	);

const allModulesDisabled: ModuleEnabledMap = selectModuleEnabledMap(undefined);

const useModuleEnabledState = (): ModuleEnabledState => {
	const { isCustomer } = useContext(AuthContext);
	const { isOnline } = useNetworkStatus();
	const tenantId = useAppSelector((state) => state.settings.tenantId);
	const featureFlagCache = useAppSelector((state) => state.featureFlagCache);
	const { flags, isError } = useGetAllFeatureFlagsQuery(undefined, {
		selectFromResult: ({ data, isError }) => ({ flags: data, isError }),
		skip: isCustomer
	});
	const hasNoLiveAnswer = flags === undefined && (isError || !isOnline);

	return useMemo(() => {
		if (isCustomer) return { enabledByKey: allModulesDisabled, isSettled: true };
		if (hasNoLiveAnswer)
			return { enabledByKey: selectLastKnownModuleEnabledMap(featureFlagCache, tenantId), isSettled: true };
		return { enabledByKey: selectModuleEnabledMap(flags), isSettled: flags !== undefined };
	}, [featureFlagCache, flags, hasNoLiveAnswer, isCustomer, tenantId]);
};

export type { ModuleEnabledState };
export default useModuleEnabledState;
